/* ============================================================
   capture.js — Klaviyo email + SMS capture.

   Why this matters more than a normal newsletter box: this store will never
   have customer accounts, so the owned email/SMS list is the only direct line
   to customers and the brand's fallback if Shopify, Instagram or Discord
   access is ever lost (docs/09).

   Progressive enhancement, in three layers:
     1. No JS at all      → the form is a real {% form 'customer' %} and POSTs
                            to Shopify's native newsletter signup.
     2. No Klaviyo key    → this file is never even loaded (theme.liquid only
                            emits the <script> once a key is set), so layer 1
                            stands.
     3. JS + key          → we intercept and POST to Klaviyo's client API.

   API shape verified against developers.klaviyo.com (revision 2026-07-15):
     POST https://a.klaviyo.com/client/subscriptions/?company_id=<public key>
     Content-Type: application/vnd.api+json   ·  no Authorization header
     202 Accepted, empty body.

   Klaviyo lists default to DOUBLE opt-in, so the success copy says "check
   your email to confirm" rather than claiming the subscription is complete.

   All user-facing strings arrive as data-* attributes from Liquid so every
   word stays editable in the theme editor — nothing is hardcoded here.

   Markup contract (see snippets/klaviyo-form.liquid).

   Phase 10 added four things every capture point benefits from: channel-scoped
   consent, phone-only signups, one retry, and klaviyo.identify(). Each is
   explained where it happens.

   Phase 11 added marketing-source attribution: utm_* / fbclid captured on
   landing, held for 30 days, and merged into the signup alongside $source.
   The reasoning lives in snippets/klaviyo-form.liquid, not here.

   Last updated: 2026-09-17
   ============================================================ */
(function () {
  'use strict';

  /* A second <script> for this file would execute it twice and register the
     delegated submit listener twice — two POSTs per signup, two profiles. */
  if (window.__nmpCaptureReady) return;
  window.__nmpCaptureReady = true;

  var ENDPOINT = 'https://a.klaviyo.com/client/subscriptions/';
  /* A list signup does NOT trigger a back-in-stock flow; only this endpoint's
     metric does. Full account in snippets/klaviyo-form.liquid. */
  var BIS_ENDPOINT = 'https://a.klaviyo.com/client/back-in-stock-subscriptions/';
  var REVISION = '2026-07-15';
  var RETRY_MS = 1200;

  /* Klaviyo requires E.164. Anything we cannot confidently normalise is
     dropped and the email is sent on its own — losing a phone number is far
     better than losing the signup. */
  function toE164(raw) {
    if (!raw) return null;
    var trimmed = String(raw).trim();
    var hadPlus = trimmed.charAt(0) === '+';
    var digits = trimmed.replace(/\D/g, '');
    if (!digits) return null;
    /* E.164 caps the whole number at 15 digits. Without the upper bound a
       mistyped run of digits sailed through as "+" + 22 digits and Klaviyo
       rejected the entire call — taking the email with it, which is the one
       outcome this function exists to prevent. */
    if (hadPlus) return digits.length >= 8 && digits.length <= 15 ? '+' + digits : null;
    /* +1 is assumed for a bare 10-digit number. There was a data-country-code
       read here that no template ever emitted; a non-US market needs a real
       country picker, not a default nobody sets. */
    if (digits.length === 10) return '+1' + digits;
    if (digits.length === 11 && digits.charAt(0) === '1') return '+' + digits;
    return null;
  }

  function isEmail(value) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || '').trim());
  }

  function setMessage(scope, text, isError) {
    var box = scope.querySelector('[data-capture-message]');
    if (!box) return;
    /* Unhide BEFORE writing: the box is role="status" aria-live="polite"
       hidden, a mutation in a hidden subtree is never announced, and unhiding
       afterwards does not replay it. Written the other way round every outcome
       this form has was silent. search.js documents the same trap. */
    box.hidden = !text;
    box.classList.toggle('form-error', !!isError);
    box.textContent = text;
  }

  /* Extra profile properties, from two places.

     Fixed ones arrive as a JSON object on the wrapper: the notify-me box on a
     product page uses this to record which piece and which variant the
     address came from, so a back-in-stock send can be aimed at exactly the
     people who wanted that one (docs/09).

     Chosen ones are read off the form itself — any control carrying
     data-capture-property contributes its value under that name. The
     early-access page's size chips use this. Unchecked radios and boxes are
     skipped, so nothing is recorded for a question nobody answered.

     Anything unparseable or empty is dropped rather than allowed to fail the
     signup: losing a property is far better than losing the address. This is
     also why the native no-JavaScript path simply does not carry them —
     Shopify's newsletter POST has nowhere to put a property, and the address
     still arrives. */
  function readProperties(config, form) {
    var properties = null;

    var raw = config.getAttribute('data-properties');
    if (raw) {
      try {
        var parsed = JSON.parse(raw);
        if (parsed && typeof parsed === 'object') properties = parsed;
      } catch (e) {
        /* fall through — the chosen ones below are still worth sending */
      }
    }

    /* Chosen ones win: a form-level [data-capture-property] overwrites a
       same-named key from data-properties, because the assignment below is
       unconditional. Load-bearing for a capture point whose properties are
       only known at runtime rather than fixed in the section's own settings —
       a hidden input written by page JS rides in the same way. */
    if (form) {
      var fields = form.querySelectorAll('[data-capture-property]');
      Array.prototype.forEach.call(fields, function (field) {
        var type = (field.type || '').toLowerCase();
        if ((type === 'radio' || type === 'checkbox') && !field.checked) return;
        var value = String(field.value || '').trim();
        if (!value) return;
        if (!properties) properties = {};
        properties[field.getAttribute('data-capture-property')] = value;
      });
    }

    return properties;
  }


  /* ---- Attribution -------------------------------------------------------
     Which campaign brought someone is the one question this file could never
     answer: before this block a Meta signup and an organic one were identical
     on the profile, so paid acquisition could not be measured at all.

     The full account — last-campaign-touch vs first-touch and the one line
     that flips it, the 30-day window, why these key names rather than our own,
     and what the password page and the no-JS path can and cannot carry — is in
     snippets/klaviyo-form.liquid. Shopify strips that comment server-side, so
     the explanation costs a visitor nothing. Read it there. */
  var ATTR_KEY = 'nmp-attr';
  var ATTR_TTL = 2592000000; /* 30 days, matching Meta's default window */
  /* Klaviyo's own popup recorder writes utm_* under exactly these names, so we
     match it rather than inventing nmp_utm_*: ONE segment condition then covers
     both the popup and this file, which is the entire point of the exercise.
     fbclid rides along because a Meta ad often carries only that when the
     campaign URL is untagged — it is never translated into a synthetic
     utm_source, because that would be inventing data. */
  var ATTR_FIELDS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'fbclid'];

  function attrOn() {
    return !!(document.body && document.body.hasAttribute('data-capture-attribution'));
  }

  /* Safari's private mode throws on ACCESS, not only on write, so even naming
     localStorage has to sit inside the try. A visitor whose storage is blocked
     still signs up — they just arrive without a campaign attached. */
  function attrBox() {
    try {
      return window.localStorage;
    } catch (e) {
      return null;
    }
  }

  /* Runs on every page load, which is the whole trick: the page someone LANDS
     on is rarely the page they sign up on. Replaces the stash when — and only
     when — the URL actually carries a campaign, so browsing on from the landing
     page cannot erase it and neither can a later untagged visit. */
  function attrStash() {
    if (!attrOn()) return;
    var box = attrBox();
    if (!box) return;

    var params;
    try {
      params = new URLSearchParams(window.location.search);
    } catch (e) {
      return;
    }

    var found = null;
    ATTR_FIELDS.forEach(function (key) {
      var value = params.get(key);
      if (!value) return;
      if (!found) found = {};
      /* A profile property is not a place to store a page of junk, and an
         over-long value risks the request that carries the address. */
      found[key] = String(value).slice(0, 120);
    });
    if (!found) return;

    found.nmp_landing_path = String(window.location.pathname || '/').slice(0, 120);
    found.nmp_landed_at = new Date().toISOString();
    /* Same-origin navigation is not a referral; recording it would make every
       internal click look like one. */
    var ref = document.referrer || '';
    if (ref && ref.indexOf(window.location.origin) !== 0) found.nmp_referrer = ref.slice(0, 120);

    try {
      box.setItem(ATTR_KEY, JSON.stringify(found));
    } catch (e) {
      /* quota, or storage blocked mid-session — the signup still works */
    }
  }

  /* Merges the stash into the properties bound for Klaviyo. Never overwrites a
     key readProperties already set: the "chosen ones win" contract above still
     holds, so a section that deliberately sets one of these names keeps it. */
  function decorate(properties, source) {
    if (!attrOn()) return properties;
    if (!properties) properties = {};

    /* The same value also rides as custom_source on the consent record. That
       copy is immutable per opt-in and is the only history of where each
       consent was given; this one is last-write-wins and is what a segment can
       actually query. Both are wanted, for different questions. */
    if (source && properties.$source === undefined) properties.$source = source;
    if (properties.nmp_signup_path === undefined) {
      properties.nmp_signup_path = String(window.location.pathname || '/').slice(0, 120);
    }

    var box = attrBox();
    if (!box) return properties;
    var raw;
    try {
      raw = box.getItem(ATTR_KEY);
    } catch (e) {
      return properties;
    }
    if (!raw) return properties;

    var stash;
    try {
      stash = JSON.parse(raw);
    } catch (e) {
      return properties;
    }
    if (!stash || typeof stash !== 'object') return properties;

    /* Outside the window the campaign is no longer a credible explanation for
       this signup, so it is dropped rather than over-claimed. */
    var landed = Date.parse(stash.nmp_landed_at || '');
    if (!landed || Date.now() - landed > ATTR_TTL) return properties;

    Object.keys(stash).forEach(function (key) {
      if (stash[key] && properties[key] === undefined) properties[key] = stash[key];
    });

    return properties;
  }

  /* `channels` ('both' default / 'email' / 'phone', from data-channels) mirrors
     the calling section's `collect` setting — what the visitor was actually
     asked to consent to. Klaviyo's rule: include a `subscriptions` object and
     ONLY the channels in it are subscribed; omit it and everything defaults to
     marketing. So an identifier is sent whenever we have one — that is how the
     profile carries a backup address — but consent is recorded only for a
     channel the form asked about AND can reach. Until Phase 10 the email key
     was unconditional, so a phone-first form opted the address into email. */
  function buildPayload(listId, source, email, phone, properties, channels) {
    var wantsEmail = channels !== 'phone';
    var wantsSms = channels !== 'email';
    var subscriptions = {};
    if (email && wantsEmail) subscriptions.email = { marketing: { consent: 'SUBSCRIBED' } };
    if (phone && wantsSms) subscriptions.sms = { marketing: { consent: 'SUBSCRIBED' } };

    var attributes = {};
    if (email) attributes.email = email;
    if (phone) attributes.phone_number = phone;
    if (properties) attributes.properties = properties;
    /* Never send an empty object: `subscriptions: {}` is not "subscribe to
       nothing", it is a shape Klaviyo has no reason to accept. */
    if (subscriptions.email || subscriptions.sms) attributes.subscriptions = subscriptions;

    return {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: source,
          profile: { data: { type: 'profile', attributes: attributes } }
        },
        relationships: { list: { data: { type: 'list', id: listId } } }
      }
    };
  }

  /* Bind the profile to Klaviyo's onsite cookie after a subscribe. The client
     API does not cookie the browser — Klaviyo's hosted forms do, ours does not
     — so this is what gives a returning visitor's __kla_id an $exchange_id for
     any future onsite personalization to key off. Both queues are pushed:
     `klaviyo` replaced `_learnq`, but an older embed exposes only the latter.
     Whichever exists drains on load.

     ⚠ INERT until the Klaviyo app embed is enabled on the store: nothing here
     loads the onsite script, and nothing may — CLAUDE.md forbids external JS.
     Written correct-and-dormant so identification starts working the day the
     owner enables it. */
  function identify(email, phone) {
    var traits = {};
    if (email) traits.$email = email;
    if (phone) traits.$phone_number = phone;
    if (!traits.$email && !traits.$phone_number) return;
    try {
      window.klaviyo = window.klaviyo || [];
      window.klaviyo.push(['identify', traits]);
      window._learnq = window._learnq || [];
      window._learnq.push(['identify', traits]);
    } catch (e) {
      /* An embed that replaced the global with something non-pushable is not
         worth losing a captured address over. */
    }
  }

  /* One POST, one retry, for the two failures worth repeating. Safe here and
     not in cart.js because this endpoint upserts on the identifier: twice
     yields one profile, where /cart/add.js twice yields two of the item.
     `reason` is the taxonomy callers branch on; it rides out on
     nmp:capture:error so any section can render its own failure state
     instead of the shared message box. */
  function send(url, body, attempt, done) {
    window.fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/vnd.api+json', revision: REVISION },
      body: body
    })
      .then(function (response) {
        if (response.status === 202 || response.ok) return done(null);
        if (response.status === 429 && attempt === 0) {
          window.setTimeout(function () { send(url, body, 1, done); }, RETRY_MS);
          return;
        }
        /* A 4xx is a statement about the payload — repeating it changes
           nothing. A 5xx is Klaviyo having a bad minute. Different sentences. */
        done(response.status >= 500 ? 'server' : 'rejected');
      }, function () {
        /* Second argument, not .catch (2026-09-07): chained after the handler
           above, a throw inside done() read as a network failure — one more
           POST and a second done() call for a bug that was never the network. */
        if (attempt === 0) {
          window.setTimeout(function () { send(url, body, 1, done); }, RETRY_MS);
          return;
        }
        done('network');
      });
  }

  /* Channels mirror buildPayload so notification never exceeds consent.
     Klaviyo takes only email / phone_number / external_id here — `properties`
     cannot ride this request. See snippets/klaviyo-form.liquid. */
  function buildBackInStock(variantId, email, phone, channels) {
    var wanted = [];
    if (email && channels !== 'phone') wanted.push('EMAIL');
    if (phone && channels !== 'email') wanted.push('SMS');

    var attributes = {};
    if (email) attributes.email = email;
    if (phone) attributes.phone_number = phone;

    return {
      data: {
        type: 'back-in-stock-subscription',
        attributes: {
          channels: wanted,
          profile: { data: { type: 'profile', attributes: attributes } }
        },
        relationships: {
          variant: { data: { type: 'catalog-variant', id: variantId } }
        }
      }
    };
  }

  /* `form` is the <form> being submitted; `config` is its nearest
     [data-nmp-capture] wrapper, which carries the Klaviyo IDs and every
     user-facing string (see snippets/klaviyo-form.liquid). */
  function submit(form, config, event) {
    var company = config.getAttribute('data-company');
    var listId = config.getAttribute('data-list');
    var variantId = config.getAttribute('data-variant');

    /* No company, or neither identifier to send to: let the browser post the
       native form rather than swallow the signup. A variant ALONE is enough —
       that is what makes notify-me work while klaviyo_list_early is unset. */
    if (!company) return;
    if (!listId && !variantId) return;

    var emailField = form.querySelector('[name="contact[email]"], [type="email"]');
    var phoneField = form.querySelector('[name="contact[phone]"], [type="tel"]');
    var email = emailField ? emailField.value.trim() : '';
    var rawPhone = phoneField ? phoneField.value.trim() : '';
    var phone = toE164(rawPhone);

    /* A typed address that is not an address is always an error, even when a
       usable phone number sits beside it. Dropping it silently would leave the
       visitor believing their backup address had registered — and on this
       feature the backup address is where the discount code goes. */
    if (email !== '' && !isEmail(email)) {
      event.preventDefault();
      /* Every user-facing string lives on `config`, the [data-nmp-capture]
         wrapper — never on the <form>. This line read it off the form until
         Phase 5, which returned null, and setMessage hides the box whenever
         the text is empty. The result was a signup that failed in silence on
         the one path a shopper is most likely to hit. */
      setMessage(config, config.getAttribute('data-msg-invalid-email'), true);
      if (emailField) {
        emailField.classList.add('field--invalid');
        emailField.focus();
      }
      return;
    }

    /* One usable identifier is the real requirement — which one is the
       section's business, not this file's. Until Phase 10 a valid email was
       mandatory, so a phone-first form could not submit at all. */
    if (email === '' && phone === null) {
      event.preventDefault();
      setMessage(config, config.getAttribute('data-msg-no-identifier'), true);
      var firstField = phoneField || emailField;
      if (firstField) firstField.focus();
      return;
    }

    event.preventDefault();
    if (emailField) emailField.classList.remove('field--invalid');

    /* Only worth saying when the email is what actually got through. */
    var phoneDropped = rawPhone !== '' && phone === null && email !== '';

    /* aria-disabled stops the mouse, not Enter — every press queued another
       POST, and a POST cannot be aborted (see cart.js). */
    if (form.hasAttribute('data-capture-busy')) return;
    form.setAttribute('data-capture-busy', '');

    var button = form.querySelector('[type="submit"]');
    if (button) button.setAttribute('aria-disabled', 'true');
    setMessage(config, config.getAttribute('data-msg-sending'), false);

    var channels = config.getAttribute('data-channels') || 'both';
    var query = '?company_id=' + encodeURIComponent(company);

    function finish(reason) {
      if (button) button.removeAttribute('aria-disabled');

      if (!reason) {
        identify(email, phone);
        var success = config.getAttribute('data-msg-success') || '';
        if (phoneDropped) {
          success += ' ' + (config.getAttribute('data-msg-phone-dropped') || '');
        }
        form.setAttribute('data-capture-state', 'success');
        setMessage(config, success.trim(), false);
        form.dispatchEvent(
          new CustomEvent('nmp:capture:success', {
            bubbles: true,
            detail: { email: email, phone: phone, droppedPhone: phoneDropped }
          })
        );
        /* Guard stays set — a retry would only duplicate the profile. */
        return;
      }

      form.removeAttribute('data-capture-busy');
      /* Three failures, three sentences. A rejected payload is the shopper's
         to fix; a 5xx and a dead connection are not, and telling someone their
         address looks wrong when it does not is worse than useless.
         data-msg-rejected falls back to the generic line so every wrapper
         written before this existed keeps working unchanged.

         ⚠ NOT on the back-in-stock path: a 4xx there means an unrecognised
         catalog variant, not a bad number. See snippets/klaviyo-form.liquid. */
      var text =
        reason === 'network'
          ? config.getAttribute('data-msg-offline')
          : reason === 'rejected' && !variantId
            ? config.getAttribute('data-msg-rejected') || config.getAttribute('data-msg-error')
            : config.getAttribute('data-msg-error');
      setMessage(config, text, true);
      form.dispatchEvent(
        new CustomEvent('nmp:capture:error', { bubbles: true, detail: { reason: reason } })
      );
    }

    /* Both present means two requests, and the BIS one drives the UI because
       it is the one that creates the metric. The list call records the SMS
       consent Klaviyo needs before the flow's text branch can send; its
       outcome is not reported. Why, in snippets/klaviyo-form.liquid. */
    if (listId) {
      var source = config.getAttribute('data-source') || 'theme';
      send(
        ENDPOINT + query,
        JSON.stringify(
          buildPayload(
            listId,
            source,
            email,
            phone,
            decorate(readProperties(config, form), source),
            channels
          )
        ),
        0,
        variantId ? function () {} : finish
      );
    }

    if (variantId) {
      send(BIS_ENDPOINT + query, JSON.stringify(buildBackInStock(variantId, email, phone, channels)), 0, finish);
    }
  }

  /* Before the listener, not inside it: the campaign is on the URL of the page
     the visitor LANDED on, which is usually not the page they submit from. */
  attrStash();

  document.addEventListener('submit', function (event) {
    var form = event.target;
    if (!form || !form.matches || !form.matches('form')) return;
    var config = form.closest('[data-nmp-capture]');
    if (config) submit(form, config, event);
  });
})();
